
import React, { useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useLeads } from "@/context/LeadContext";
import { usePartners } from "@/context/PartnerContext";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  FileBarChart2,
  Calendar,
  TrendingUp,
  Users,
  Target,
  MessageSquare,
  CheckCircle
} from "lucide-react";

const Reports = () => {
  const { leads } = useLeads();
  const { partners } = usePartners();
  const [timeRange, setTimeRange] = useState("all");

  // Filter leads based on time range
  const filteredLeads = (() => {
    if (timeRange === "all") return leads;

    const now = new Date();
    const cutoffDate = new Date();

    switch (timeRange) {
      case "week":
        cutoffDate.setDate(now.getDate() - 7);
        break;
      case "month":
        cutoffDate.setMonth(now.getMonth() - 1);
        break;
      case "quarter":
        cutoffDate.setMonth(now.getMonth() - 3);
        break;
      default:
        return leads;
    }

    return leads.filter(lead => new Date(lead.createdAt) >= cutoffDate);
  })();

  // Calculate stage distribution
  const stageData = [
    {
      name: "Σε εκκρεμότητα",
      value: filteredLeads.filter(lead => lead.stage === "σεΕκκρεμότητα").length,
      color: "#f59e0b"
    },
    {
      name: "Επιτυχία",
      value: filteredLeads.filter(lead => lead.stage === "έκλεισεΗΔουλειά").length,
      color: "#10b981"
    },
    {
      name: "Πληροφορίες",
      value: filteredLeads.filter(lead => lead.stage === "ήθελεΠληροφορίες").length,
      color: "#6366f1"
    },
  ];

  // Calculate service distribution
  const serviceData = [
    { name: "Εξοικονομώ", value: filteredLeads.filter(lead => lead.service === "εξοικονομώ").length, fill: "#3b82f6" },
    { name: "Αντλίες", value: filteredLeads.filter(lead => lead.service === "αντλίεςΘερμότητας").length, fill: "#8b5cf6" },
    { name: "Φωτοβολταϊκά", value: filteredLeads.filter(lead => lead.service === "φωτοβολταϊκά").length, fill: "#10b981" },
    { name: "Κλιματιστικά", value: filteredLeads.filter(lead => lead.service === "κλιματιστικά").length, fill: "#f59e0b" },
    { name: "Άλλο", value: filteredLeads.filter(lead => lead.service === "άλλο").length, fill: "#6b7280" },
  ];

  // Calculate partner performance
  const partnerData = partners.map(partner => {
    const partnerLeads = filteredLeads.filter(lead => lead.partnerId === partner.id);
    const closedLeads = partnerLeads.filter(lead => lead.stage === "έκλεισεΗΔουλειά").length;

    return {
      name: partner.name.length > 12 ? partner.name.substring(0, 12) + '...' : partner.name,
      leads: partnerLeads.length,
      success: closedLeads,
      rate: partnerLeads.length > 0 ? Math.round((closedLeads / partnerLeads.length) * 100) : 0,
    };
  }).sort((a, b) => b.leads - a.leads).slice(0, 8);

  // Stats calculations
  const successRate = filteredLeads.length > 0
    ? Math.round((filteredLeads.filter(lead => lead.stage === "έκλεισεΗΔουλειά").length / filteredLeads.length) * 100)
    : 0;
  const smsSent = filteredLeads.filter(lead => lead.smsStatus === "sent" || lead.smsStatus === "delivered").length;

  const timeRangeOptions = [
    { value: "all", label: "Όλα τα δεδομένα" },
    { value: "week", label: "Τελευταία εβδομάδα" },
    { value: "month", label: "Τελευταίος μήνας" },
    { value: "quarter", label: "Τελευταίο τρίμηνο" },
  ];

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Page Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-orange-500 to-red-600 shadow-lg">
            <FileBarChart2 className="h-6 w-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl lg:text-3xl font-bold text-slate-900">Αναφορές</h1>
            <p className="text-slate-500 mt-0.5">Αναλυτικά στατιστικά απόδοσης</p>
          </div>
        </div>

        <Select value={timeRange} onValueChange={setTimeRange}>
          <SelectTrigger className="w-52 bg-white border-slate-200">
            <Calendar className="h-4 w-4 mr-2 text-slate-400" />
            <SelectValue placeholder="Περίοδος" />
          </SelectTrigger>
          <SelectContent>
            {timeRangeOptions.map(option => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-50">
                <Target className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{filteredLeads.length}</p>
                <p className="text-xs text-slate-500">Σύνολο Επαφών</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-emerald-50">
                <TrendingUp className="h-5 w-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-emerald-600">{successRate}%</p>
                <p className="text-xs text-slate-500">Ποσοστό Επιτυχίας</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-violet-50">
                <Users className="h-5 w-5 text-violet-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{partners.length}</p>
                <p className="text-xs text-slate-500">Συνεργάτες</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-amber-50">
                <MessageSquare className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{smsSent}</p>
                <p className="text-xs text-slate-500">SMS Απεσταλμένα</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2">
              <div className="p-2 rounded-lg bg-amber-50">
                <Target className="h-4 w-4 text-amber-600" />
              </div>
              <div>
                <CardTitle className="text-lg">Κατάσταση Leads</CardTitle>
                <CardDescription>Κατανομή ανά στάδιο</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={stageData}
                  cx="50%"
                  cy="50%"
                  innerRadius={65}
                  outerRadius={95}
                  paddingAngle={4}
                  dataKey="value"
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  labelLine={{ stroke: '#94a3b8' }}
                >
                  {stageData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value, name) => [value, name]}
                  contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0' }}
                />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2">
              <div className="p-2 rounded-lg bg-blue-50">
                <FileBarChart2 className="h-4 w-4 text-blue-600" />
              </div>
              <div>
                <CardTitle className="text-lg">Leads ανά Υπηρεσία</CardTitle>
                <CardDescription>Κατανομή ενδιαφέροντος</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={serviceData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={true} vertical={false} />
                <XAxis type="number" tick={{ fontSize: 12 }} stroke="#94a3b8" />
                <YAxis type="category" dataKey="name" width={85} tick={{ fontSize: 12 }} stroke="#94a3b8" />
                <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0' }} />
                <Bar dataKey="value" name="Επαφές" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Partner Performance */}
      <Card className="border-slate-200">
        <CardHeader className="pb-2">
          <div className="flex items-center gap-2">
            <div className="p-2 rounded-lg bg-emerald-50">
              <Users className="h-4 w-4 text-emerald-600" />
            </div>
            <div>
              <CardTitle className="text-lg">Απόδοση Συνεργατών</CardTitle>
              <CardDescription>Σύγκριση leads και επιτυχιών</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="h-96">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={partnerData}
              layout="vertical"
              margin={{ top: 10, right: 30, left: 10, bottom: 10 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={true} vertical={false} />
              <XAxis type="number" tick={{ fontSize: 12 }} stroke="#94a3b8" />
              <YAxis type="category" dataKey="name" width={100} tick={{ fontSize: 11 }} stroke="#94a3b8" />
              <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0' }} />
              <Legend />
              <Bar dataKey="leads" name="Leads" fill="#3b82f6" radius={[0, 4, 4, 0]} />
              <Bar dataKey="success" name="Επιτυχίες" fill="#10b981" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
    </div>
  );
};

export default Reports;
